import { useEffect, useRef, useCallback } from 'react';
import { isAbortError } from '../lib/apiErrorHandling';

/**
 * Reusable polling hook for periodic data fetching.
 * 
 * @param fetchFn - Function to call for polling
 * @param intervalMs - Polling interval in milliseconds
 * @param enabled - Whether polling is enabled (default: true)
 * @param immediate - Whether to fetch immediately on mount (default: true)
 * @returns Object with manual refresh function and abort controller
 */
export function usePolling(
  fetchFn: () => Promise<void>,
  intervalMs: number,
  enabled: boolean = true,
  immediate: boolean = true
) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Use ref to track if a request is in progress to prevent concurrent requests
  const loadingRef = useRef(false);
  // Use ref to store latest fetchFn to avoid dependency issues
  const fetchFnRef = useRef(fetchFn);
  const enabledRef = useRef(enabled);

  // Update refs when values change
  useEffect(() => {
    fetchFnRef.current = fetchFn;
    enabledRef.current = enabled;
  }, [fetchFn, enabled]);

  const executeFetch = useCallback(async () => {
    if (!enabledRef.current) return;

    // Prevent concurrent requests
    if (loadingRef.current) {
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    loadingRef.current = true;

    try {
      await fetchFnRef.current();
    } catch (err) {
      // Silently handle abort errors
      if (isAbortError(err)) {
        return;
      }
      // Other errors should be handled by the fetchFn or caller
    } finally {
      loadingRef.current = false;
    }
  }, []); // No dependencies - uses refs instead

  const manualRefresh = useCallback(async () => {
    await executeFetch();
  }, [executeFetch]);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      abortControllerRef.current?.abort();
      return;
    }

    // Immediate fetch on mount/enable
    if (immediate) {
      executeFetch();
    }

    // Set up polling interval
    intervalRef.current = setInterval(() => {
      executeFetch();
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      abortControllerRef.current?.abort();
    };
  }, [enabled, intervalMs, immediate, executeFetch]);

  return {
    manualRefresh,
    abortController: abortControllerRef.current,
  };
}

